import React from "react"
import * as Yup from "yup"
import {
  Avatar, Box, Button, Flex, FormControl, FormHelperText, FormLabel, Heading, Input,
  InputGroup, InputRightElement, SimpleGrid, Stack, Text,
} from "@chakra-ui/react"
import _ from "lodash"
import useCurrentAccount from "services/useCurrentAccount"
import { useCurrentEvent, useEvents } from "queries/events"
import {
  EventUserRole,
  ACCOUNT_PERMISSIONS_TITLE, EVENT_ROLE_TITLE, ADMIN_PERMISSION, CAN_CREATE_EVENTS_PERMISSION,
} from "services/permissions"
import { asDate } from "utilities/strings"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import EventWithAvatar from "components/elements/EventWithAvatar"
import Checkbox from "components/ReactHookForm/Checkbox"
import Select from "components/ReactHookForm/Select"
import TextField from "components/ReactHookForm/TextField"
import ImageUpload from "components/ReactHookForm/ImageUpload"
import PhoneNumberField from "components/inputs/PhoneNumberField"
import { SegmentedControl } from "components/SegmentedControl"
import Icon from "components/elements/Icon"
import RefreshIcon from "components/elements/icons/RefreshIcon"
import CopyToClipboard from "components/elements/CopyToClipboard"
import { User } from "sharedTypes"
import { useController } from "react-hook-form"
import RadioCard from "components/inputs/RadioCard"
import RadioCards from "components/ReactHookForm/RadioCards"

export type Mode = "invite" | "create"

export type InitialValuesProps = {
  admin: boolean
  canCreateEvents: boolean
  eventId?: string
  role?: EventUserRole
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  password?: string
  photo?: File
  photoUrl?: string
}

type Props = {
  initialValues: InitialValuesProps
  onSubmit: (values) => Promise<any>
  onClose: () => void
  currentUser: User
  user?: User
  mode: Mode
  onModeChange: (mode: Mode) => void
}

const WORDS = [
  "Apple", "Badge", "Coral", "Delta", "Eagle", "Floor", "Globe", "Haven",
  "Karma", "Lemon", "Maple", "Noble", "Opera", "Plaza", "Quest", "Ridge",
  "Stage", "Table", "Unity", "Vista", "Cedar", "Drake", "Ember", "Frost",
]

const generatePassword = () => {
  const year = new Date().getFullYear()
  const a = WORDS[Math.floor(Math.random() * WORDS.length)]
  const b = WORDS[Math.floor(Math.random() * WORDS.length)]

  return `${a}-${b}-${year}!`
}

const ROLES = [
  {
    value: EventUserRole.EventAdmin,
    name: "Event Administrator",
    description: "Full access to all event settings, features, guest data, and team permissions.",
  },
  {
    value: EventUserRole.GuestManager,
    name: "Guest Manager",
    description: "Manage guests, emails, seating, registration forms, and submissions. Can process payments through the mobile app.",
  },
  {
    value: EventUserRole.DoorOperator,
    name: "Door Operator",
    description: "Check in guests, view seating charts, and process point-of-sale payments through the mobile app.",
  },
  {
    value: EventUserRole.DoorStaff,
    name: "Door Staff",
    description: "Check in guests only. Guest email addresses and phone numbers are hidden.",
  },
  {
    value: EventUserRole.WebsiteAdmin,
    name: "Content Manager",
    description: "Manage website content, event agenda, registration forms, and guest portal customization. No access to guest data.",
  },
  {
    value: EventUserRole.NoAccess,
    name: "No Access",
    description: "This team member cannot access this event.",
  },
]

const PasswordField = ({ control }: { control: any }) => {
  const { field } = useController({ name: "password", control })

  return (
    <FormControl>
      <Flex justify="space-between" align="center" mb={1}>
        <FormLabel mb={0}>Password</FormLabel>
        <Button
          variant="link"
          size="sm"
          colorScheme="blue"
          onClick={() => field.onChange(generatePassword())}
          leftIcon={<RefreshIcon fontSize="0.75rem" />}
        >
          Generate
        </Button>
      </Flex>
      <InputGroup>
        <Input
          {...field}
          value={field.value ?? ""}
          type="text"
          variant="outline"
          placeholder="Set a password"
        />
        <InputRightElement>
          <CopyToClipboard
            text={field.value ?? ""}
            copiedContent={<Icon icon="checkmark" size={4} color="green.500" />}
            variant="ghost"
            size="sm"
            color="gray.500"
            isDisabled={!field.value}
            aria-label="Copy password"
          >
            <Icon icon="copy" size={4} />
          </CopyToClipboard>
        </InputRightElement>
      </InputGroup>
      <FormHelperText>Minimum 12 characters</FormHelperText>
    </FormControl>
  )
}

const PermissionsForm = ({
  initialValues, onSubmit, onClose, currentUser, user, mode, onModeChange,
}: Props) => {
  const currentAccount = useCurrentAccount()
  const { data: currentEvent } = useCurrentEvent()
  const { data: events = [] } = useEvents(currentAccount.id)
  const event = events.find(({ id }) => id === initialValues.eventId)
  const existing = !!initialValues.email

  const showProfileFields = existing || mode === "create"
  const showPasswordField = !existing && mode === "create"

  const validationShape: Record<string, any> = {
    canCreateEvents: Yup.boolean(),
    eventId: Yup.string(),
    role: Yup.string().when("eventId", {
      is: (val) => !!val,
      then: (schema) => schema.required(),
    }),
  }

  if (!existing) {
    validationShape.email = Yup.string().email().required()
  }
  if (!existing && mode === "create") {
    validationShape.password = Yup.string().min(12).required()
    validationShape.firstName = Yup.string().required()
    validationShape.lastName = Yup.string().required()
  }

  const EventUserSchema = Yup.object().shape(validationShape)

  const roles = existing ? ROLES : ROLES.filter((r) => r.value !== EventUserRole.NoAccess)

  const submitText = existing ? "Save" : mode === "invite" ? "Send Invite" : "Create User"

  return (
    <ModalForm
      size="xl"
      title="Team Member"
      initialValues={initialValues}
      submitText={submitText}
      onSubmit={onSubmit}
      onClose={onClose}
      validationSchema={EventUserSchema}
      disableSubmitWhenNotDirty={existing}
    >
      {({ control, watch, setValue }) => {
        const [admin] = watch(["admin"])

        const handleChangeAdmin = async (e) => {
          const value = e.target.checked

          if (value) {
            await setValue("canCreateEvents", true)
          }
          setValue("admin", value)
        }

        return (
          <Stack spacing={4}>
            {!existing && (
              <>
                <SegmentedControl
                  options={[
                    { label: <><Icon icon="email-plain" size={3} style={{ marginRight: 6 }} />Send Invite</>, value: "invite" },
                    { label: <><Icon icon="guest-profile" size={3} style={{ marginRight: 6 }} />Create Directly</>, value: "create" },
                  ]}
                  value={mode}
                  onChange={(v) => onModeChange(v as Mode)}
                  width="full"
                />
                <Text>
                  {mode === "invite"
                    ? "Send an email invitation so the team member can create their own password and join your account."
                    : "Create an account for the team member and set their password yourself."}
                </Text>
              </>
            )}

            {existing && user && (
              <Flex align="center" gap={3} pb={2}>
                <Avatar
                  size="md"
                  src={user.photoUrl}
                  name={`${user.firstName} ${user.lastName}`}
                  borderRadius="lg"
                />
                <Box lineHeight={6}>
                  <Flex align="center" gap={2}>
                    <Text fontSize="xl" fontWeight="semibold">{user.firstName} {user.lastName}</Text>
                    {user.id === currentUser.id && <Text fontSize="sm" color="gray.500">(You)</Text>}
                  </Flex>
                  <Text fontSize="md">{user.email}</Text>
                </Box>
              </Flex>
            )}

            {!existing && (
              <TextField
                label="Email Address"
                name="email"
                control={control}
                placeholder="Email address"
                type="email"
                autoComplete="email"
                variant="outline"
                vertical
                mb={0}
              />
            )}

            {showProfileFields && (
              <Stack spacing={4}>
                {showPasswordField && <PasswordField control={control} />}
                <SimpleGrid columns={2} spacing={4}>
                  <TextField name="firstName" label="First Name" control={control} placeholder="First Name" variant="outline" vertical mb={0} />
                  <TextField name="lastName" label="Last Name" control={control} placeholder="Last Name" variant="outline" vertical mb={0} />
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <PhoneNumberField name="phone" control={control} defaultCountry="US" />
                  </FormControl>
                  <ImageUpload
                    name="photo"
                    label="Profile Photo"
                    control={control}
                    imageUrl={initialValues.photoUrl?.includes("data.diobox.com") ? "" : initialValues.photoUrl}
                    vertical
                    imageWidth="100px"
                    imageHeight="100px"
                    imageFit="cover"
                  />
                </SimpleGrid>
              </Stack>
            )}

            {event && !currentEvent
              ? (
                <Flex align="center">
                  <Text w={1 / 5} flexShrink={0} fontWeight="bold">Event</Text>
                  <EventWithAvatar event={event} />
                </Flex>
              )
              : (
                <>
                  <Box mt={4}>
                    <Heading fontSize="xl" fontWeight="medium" mb={6}>{ACCOUNT_PERMISSIONS_TITLE}</Heading>
                    <Stack spacing={4}>
                      <Checkbox
                        name="admin"
                        control={control}
                        mb={0}
                        label={(
                          <>
                            <Text mb={1} fontWeight="semibold">{ADMIN_PERMISSION.label}</Text>
                            <Text fontSize="sm" color="gray.700">{ADMIN_PERMISSION.description}</Text>
                          </>
                        )}
                        onChange={handleChangeAdmin}
                      />
                      <Checkbox
                        name="canCreateEvents"
                        control={control}
                        mb={0}
                        label={(
                          <>
                            <Text mb={1} fontWeight="semibold">{CAN_CREATE_EVENTS_PERMISSION.label}</Text>
                            <Text fontSize="sm" color="gray.700">{CAN_CREATE_EVENTS_PERMISSION.description}</Text>
                          </>
                        )}
                        disabled={admin}
                      />
                    </Stack>
                  </Box>
                </>
              )}

            {!admin && (
              <Box mt={4}>
                <Heading fontSize="xl" fontWeight="medium" mb={6}>{EVENT_ROLE_TITLE}</Heading>
                {!event && (
                  <Select
                    name="eventId"
                    control={control}
                    options={
                      _.orderBy(events, "startDate", "desc")
                        .map(({ id, title, startDate }) => ({
                          label: `${asDate(startDate, "yyyy-MM-dd")} ${_.truncate(title, { length: 54 })}`,
                          value: id,
                        }))
                    }
                    includeBlank
                    blankLabel="Select event..."
                    fullWidth
                  />
                )}
                <RadioCards name="role" control={control} isAttached>
                  {roles.map((role) => (
                    <RadioCard
                      key={role.value}
                      value={role.value}
                      title={role.name}
                      description={role.description}
                    />
                  ))}
                </RadioCards>
              </Box>
            )}

          </Stack>
        )
      }}
    </ModalForm>
  )
}

export default PermissionsForm
